import { type ReactNode } from "react"
import { WarningCircle } from "@phosphor-icons/react"

import { cn } from "~/utils/helpers"
import { Badge } from "~/components/design-system/components"

// ---------------------------------------------------------------------------
// AllocationStatusBadge
// ---------------------------------------------------------------------------

type AllocationStatus = "needs-replenishment" | "healthy" | "overstock"

type AllocationStatusBadgeProps = {
  status: AllocationStatus
  units: number
}

const statusConfig: Record<
  AllocationStatus,
  { variant: "yellow" | "green" | "violet"; label: string }
> = {
  "needs-replenishment": { variant: "yellow", label: "Needs replenishment" },
  healthy: { variant: "green", label: "Healthy" },
  overstock: { variant: "violet", label: "Overstock" },
}

export function AllocationStatusBadge({ status, units }: AllocationStatusBadgeProps) {
  const cfg = statusConfig[status]
  const label = status === "healthy" ? cfg.label : `${cfg.label} (${units}u)`
  return <Badge variant={cfg.variant}>{label}</Badge>
}

// ---------------------------------------------------------------------------
// StoreClassBadge
// ---------------------------------------------------------------------------

type StoreClassBadgeProps = {
  classification: string
}

const classColors: Record<string, string> = {
  A: "bg-badge-green-bg text-badge-green-fg-text",
  B: "bg-badge-blue-bg text-badge-blue-fg-text",
  C: "bg-badge-orange-bg text-badge-orange-fg-text",
  D: "bg-badge-red-bg text-badge-red-fg-text",
  Outlet: "bg-badge-violet-bg text-badge-violet-fg-text",
}

export function StoreClassBadge({ classification }: StoreClassBadgeProps) {
  const color = classColors[classification] ?? "bg-badge-neutral-bg text-badge-neutral-fg-text"
  return (
    <span
      className={cn(
        "inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
        color,
      )}
    >
      {classification.charAt(0)}
    </span>
  )
}

// ---------------------------------------------------------------------------
// DaysOfStockIndicator
// ---------------------------------------------------------------------------

function daysOfStockColor(days: number) {
  if (days >= 42) return "bg-badge-green-fg-text"
  if (days >= 21) return "bg-badge-orange-fg-text"
  return "bg-badge-red-fg-text"
}

export function DaysOfStockIndicator({ days }: { days: number }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {days < 21 && <WarningCircle size={14} className="text-badge-red-fg-text" weight="fill" />}
      <span
        className={cn("inline-block h-2 w-6 rounded-full", daysOfStockColor(days))}
        aria-hidden
      />
    </span>
  )
}

// ---------------------------------------------------------------------------
// SellThroughBar
// ---------------------------------------------------------------------------

type SellThroughBarProps = {
  percentage: number
}

function sellThroughColor(pct: number) {
  if (pct >= 60) return "bg-badge-green-fg-text"
  if (pct >= 40) return "bg-badge-orange-fg-text"
  return "bg-badge-red-fg-text"
}

export function SellThroughBar({ percentage }: SellThroughBarProps) {
  const clamped = Math.min(100, Math.max(0, percentage))
  return (
    <span className="inline-flex items-center gap-2 text-sm text-fg-base">
      <span className="relative h-2 w-16 overflow-hidden rounded-full bg-subtle">
        <span
          className={cn("absolute inset-y-0 left-0 rounded-full", sellThroughColor(percentage))}
          style={{ width: `${clamped}%` }}
        />
      </span>
      <span>{percentage}%</span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// KpiCard
// ---------------------------------------------------------------------------

type KpiCardProps = {
  title: string
  value: string
  subtitle?: string
  indicator?: ReactNode
  className?: string
}

export function KpiCard({ title, value, subtitle, indicator, className }: KpiCardProps) {
  return (
    <div className={cn("rounded-lg border border-border-base bg-base p-4", className)}>
      <p className="text-xs text-fg-subtle">{title}</p>
      <p className="mt-1 text-xl font-semibold text-fg-base">{value}</p>
      {subtitle && <p className="mt-0.5 text-xs text-fg-subtle">{subtitle}</p>}
      {indicator && <div className="mt-2">{indicator}</div>}
    </div>
  )
}
